// Copyright 2025, University of Maryland, All Rights Reserved
import React, {useEffect, useMemo, useState} from "react"
import { DataTable } from "@/components/ui/DataTable"
import { getWorkspaceBillsColumns } from "./WorkspaceBillsColumns"
import { WorkspaceBill, WorkspaceBillAction } from '@/types/workspace-bill'
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react"
import { WorkspaceBillSearchInput } from "./WorkspaceBillSearchInput"
import { SortField, SortDirection } from '@/hooks/workspace-bills/useWorkspaceBillsWorkflow'
import { WorkspaceBillCard } from "./WorkspaceBillCard"
import { WorkspaceBillsBulkActionBar } from "./WorkspaceBillsBulkActionBar"
import { useConfig } from "@/hooks/config/useConfig"

interface WorkspaceBillsTableProps {
  workspace_bills: WorkspaceBill[]
  selected_bills: string[]
  on_select_bill: (bill_id: string) => void
  on_select_all: (bills: WorkspaceBill[]) => void
  on_process_action: (bill_id: string, action: WorkspaceBillAction, all_bill_ids?: string[]) => void
  on_show_impact_details?: (bill: WorkspaceBill) => void
  current_page: number
  page_size: number
  total_pages: number
  on_page_change: (page: number) => void
  search_query: string
  on_search_change: (value: string) => void
  is_loading: boolean  
  processing_actions: Record<string, WorkspaceBillAction>
  sort_field?: SortField
  sort_direction?: SortDirection
  on_sort_change?: (field: SortField, direction: SortDirection) => void
  session?: string | null
  total_filtered_items: number
  on_bulk_print?: () => void
}

export function WorkspaceBillsTable({
  workspace_bills = [],
  selected_bills = [],
  on_select_bill,
  on_select_all,
  on_process_action,
  on_show_impact_details,
  current_page,
  page_size,
  total_pages,
  on_page_change,
  search_query,
  on_search_change,
  is_loading,
  processing_actions,
  sort_field = null,
  sort_direction = 'desc',
  on_sort_change,
  session,
  total_filtered_items,
  on_bulk_print
}: WorkspaceBillsTableProps) {
  const [is_mobile, setIsMobile] = useState(false)
  const [is_bulk_processing, setIsBulkProcessing] = useState(false)
  const { config } = useConfig()

  // Session code from WorkspaceBillsPage (fallback to empty string if null)
  const session_code = session ?? ""

  // Update mobile state on resize
  useEffect(() => {
    const check_if_mobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint in Tailwind
    }

    check_if_mobile() // Check initially

    window.addEventListener('resize', check_if_mobile)
    return () => window.removeEventListener('resize', check_if_mobile)
  }, [])

  // IMPORTANT: Don't filter the bills here, just use them directly from props
  // The backend API is already filtering by workflow_status
  const filtered_bills = useMemo(() => {
    console.log("WorkspaceBillsTable received bills:", workspace_bills.length, "items");
    return workspace_bills;
  }, [workspace_bills, current_page, processing_actions])

  const valid_bills = filtered_bills.filter(
    (bill): bill is WorkspaceBill =>
      bill !== null && bill !== undefined && typeof bill.bill_number === 'string'
  )

  const handle_pdf_click = (url: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Pass props to getWorkspaceBillsColumns for table functionality
  const columns = getWorkspaceBillsColumns({
    selected_bills,
    on_select_bill,
    on_select_all,
    on_process_action,
    on_show_impact_details,
    handle_pdf_click,
    processing_actions,
    sort_field,
    sort_direction,
    on_sort_change,
    selected_session: session_code,
    config,
  })

  const start_item = (current_page - 1) * page_size + 1
  const end_item = Math.min(
    current_page * page_size,
    (current_page - 1) * page_size + valid_bills.length
  )
  const total_items = total_filtered_items

  return (
    <div className="space-y-4 border rounded-md relative">
      <div className="p-3 border-b">
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
          <div className="w-full sm:w-auto order-2 sm:order-1">
            <WorkspaceBillSearchInput
              value={search_query}
              onChange={on_search_change}
              placeholder="Search by Bill Number"
            />
          </div>
          <div className="w-full sm:w-auto order-1 sm:order-2">
            <WorkspaceBillsBulkActionBar
              selected_count={selected_bills.length}
              is_processing={is_bulk_processing}
              on_action={(action) => {
                if (selected_bills.length > 0) {
                  setIsBulkProcessing(true)
                  Promise.resolve(
                    on_process_action(selected_bills[0], action, selected_bills)
                  ).finally(() => setIsBulkProcessing(false))
                }
              }}
              all_selected_have_reviews={
                selected_bills.length > 0 &&
                selected_bills.every((bill_number) => {
                  const bill = valid_bills.find((b) => b.bill_number === bill_number)
                  if (!bill) return false

                  // Check if bill has a legislative review at the bill level
                  return !!bill.legislative_review_s3_key
                })
              }
              on_bulk_print={on_bulk_print}
            />
          </div>
        </div>
      </div>

      {is_mobile ? (
        // Mobile view -> use WorkspaceBillCard
        <div className="p-4 grid grid-cols-1 gap-4">
          {valid_bills.map((bill) => (
            <WorkspaceBillCard
              key={bill.bill_number}
              bill={bill}
              is_selected={selected_bills.includes(bill.bill_number)}
              session_code={session_code}
              on_select_bill={on_select_bill}
              on_process_action={on_process_action}
              on_show_impact_details={on_show_impact_details}
              handle_pdf_click={handle_pdf_click}
              processing_actions={processing_actions}
            />
          ))}
        </div>
      ) : (
        // Desktop view -> use DataTable
        <DataTable<WorkspaceBill>
          columns={columns}
          data={valid_bills}
          isLoading={is_loading}
        />
      )}

      {/* Always show the loading indicator when loading, even if there are bills */}
      {is_loading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
          <div className="flex items-center gap-2 p-2 bg-background/90 rounded shadow">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading bills...</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 border-t gap-4">
        <div className="text-sm text-muted-foreground">
          {is_loading 
            ? 'Loading bills...'
            : total_items === 0
              ? 'No bills found'
              : `Showing ${start_item} to ${end_item} of ${total_items} bills`}
          {search_query && !is_loading && total_items > 0 && ` (filtered)`}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => on_page_change(1)}
              disabled={current_page === 1 || is_loading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (current_page > 1) {
                  on_page_change(current_page - 1)
                }
              }}
              disabled={current_page === 1 || is_loading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-2 overflow-x-auto">
              {generatePageNumbers(current_page, total_pages).map((page_num, idx) =>
                page_num === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2" aria-hidden="true">
                    ...
                  </span>
                ) : (
                  <Button
                    key={page_num}
                    variant={current_page === page_num ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      on_page_change(page_num)
                    }}
                    disabled={is_loading}
                    className="min-w-[2rem] flex-shrink-0"
                    aria-label={`Go to page ${page_num}`}
                    aria-current={current_page === page_num ? 'page' : undefined}
                  >
                    {page_num}
                  </Button>
                )
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (current_page < total_pages) {
                  on_page_change(current_page + 1)
                }
              }}
              disabled={current_page === total_pages || is_loading}
              aria-label="Go to next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => on_page_change(total_pages)}
              disabled={current_page === total_pages || is_loading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to generate page numbers with ellipsis
function generatePageNumbers(current: number, total: number): (number | '...')[] {
  const pages: (number | '...')[] = []
  const max_visible_pages = 7 // total number of page buttons to show

  if (total <= max_visible_pages) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  pages.push(1)
  if (current > 3) pages.push('...')

  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i)
  }

  if (current < total - 2) pages.push('...')
  if (total > 1) pages.push(total)

  return pages
}
